/**
 * apiEnvelope — shared response unwrap helpers for the React Calendar Month
 * View slices (Phase 1, Remediation 2, Task #1).
 *
 * Why this exists: Symfony BaseApi responses are **double-wrapped**:
 *   `{ data: { data: <payload>, success: true|false, error?: string } }`
 * The outer `data` is the axios/fetch response wrapper, the inner `data`
 * is the Symfony `BaseViewResponse` envelope. Existing project code at
 * `source/js/modules/org-dashboard/organization.actions.js:23-32` and
 * `source/js/modules/resources/ResourcesRouter.js:29-31` already follows
 * the same pattern; this util pulls the logic into one place so the
 * `bootstrapSlice` and `refsSlice` agree on shape handling.
 *
 * Supported shapes:
 *   1. Double envelope:  { data: { data: payload, success: true } }
 *   2. Single envelope:  { data: payload }
 *   3. Raw payload:      payload
 *   4. Error envelope:   { data: { success: false, error: 'reason' } }
 *      → throws an Error so the caller's try/catch (or thunk
 *        `rejectWithValue`) routes the message into a user-visible toast.
 *
 * @module apiEnvelope
 */

/**
 * Strip the BaseApi envelope(s) and return the raw payload.
 *
 * Throws when an explicit `success: false` envelope arrives (after
 * unwrapping the outer axios layer). Returns `null` for null/undefined
 * input — callers shouldn't have to defend against that.
 *
 * @param {*} response
 * @returns {*}
 */
export function unwrap(response) {
	if (response === null || response === undefined) return null;

	// Layer 1 — outer axios wrapper. Once unwrapped, `inner` is what the
	// PHP backend's BaseViewResponse actually returned (or, when the API
	// uses raw responses, the bare payload).
	const inner =
		typeof response === 'object' && 'data' in response ? response.data : response;

	if (inner === null || inner === undefined) return null;

	// Layer 2 — Symfony BaseViewResponse envelope
	// `{ success, data?, error? }`. Detect by presence of `success` OR
	// presence of a nested `data` field with no other obvious payload
	// fields (the latter handles routes whose envelope doesn't include
	// `success` for backward compatibility).
	if (typeof inner === 'object' && !Array.isArray(inner)) {
		if (inner.success === false) {
			throw new Error(inner.error || 'API returned success: false');
		}
		if (inner.success === true || 'data' in inner) {
			return inner.data === undefined ? null : inner.data;
		}
	}

	return inner;
}

/**
 * List-aware variant of `unwrap`. Always returns an array — useful for the
 * 5 list endpoints in `refsSlice` where the slice cares about a uniform
 * `Array<Object>` shape.
 *
 * Supported list shapes (after envelope unwrapping):
 *   - bare array            → returned as-is
 *   - key-aware extraction  → `payload[key]` when `key` is provided
 *   - `{ items: [...] }`    → returned as `items`
 *   - `{ data: [...] }`     → returned as `data` (rare, but legacy
 *                              endpoints sometimes nest twice)
 *   - object map            → `Object.values(payload)`
 *   - anything else / null  → `[]`
 *
 * @param {*} response
 * @param {string} [key] - Optional property name to extract from the
 *   unwrapped payload. Symfony endpoints often return named wrapper objects
 *   (e.g. `{pilots: [...]}`, `{resources: [...], resourcesTypes: [...]}`);
 *   passing the key avoids the `Object.values()` fallback that would
 *   flatten multiple arrays together.
 * @returns {Array<*>}
 */
export function unwrapList(response, key) {
	let payload;
	try {
		payload = unwrap(response);
	} catch (err) {
		// Re-throw so list-fetching thunks can route the error message into
		// `rejectWithValue` the same way the bootstrap thunk does.
		throw err;
	}
	if (payload === null || payload === undefined) return [];

	// Key-aware extraction: when the caller specifies a named key and the
	// unwrapped payload contains it, extract that property directly instead
	// of falling through to the Object.values() heuristic.
	if (key && typeof payload === 'object' && !Array.isArray(payload) && key in payload) {
		const value = payload[key];
		return Array.isArray(value) ? value : [value];
	}

	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload.items)) return payload.items;
	if (Array.isArray(payload.data)) return payload.data;
	if (typeof payload === 'object') return Object.values(payload);
	return [];
}
